import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Oct 2026 — "Call Exposure" banner for My Portfolio: dollars sitting in
// above-par callable preferreds / baby bonds and the instant loss if the
// issuers redeemed every one at par today. Re-fetches on portfolio changes.
const fmtUsd = (n) => `$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function statusLabel(p) {
  if (p.status === 'callable_now') return 'Callable now';
  if (p.status === 'issuer_choice') return "Issuer's choice";
  return `Call in ${p.days_to_call}d`;
}

export default function PortfolioCallExposureBanner({ onStockClick }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      let portfolioId = null;
      try {
        const mod = await import('@/lib/portfolioStore');
        portfolioId = mod.getSnapshot()?.activeId || null;
      } catch { /* free tier */ }
      const params = portfolioId ? { portfolio_id: portfolioId } : {};
      const res = await axios.get(`${API}/portfolio/call-exposure`, { params, withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener('portfolio-changed', load);
    return () => window.removeEventListener('portfolio-changed', load);
  }, [load]);

  if (!data || !data.positions?.length) return null;
  const worst = data.positions.some((p) => p.status !== 'call_soon');

  return (
    <div
      className={`mb-3 rounded-lg border p-3 ${worst ? 'border-red-400/60 bg-red-50/70 dark:bg-red-950/25' : 'border-amber-400/60 bg-amber-50/70 dark:bg-amber-950/25'}`}
      data-testid="portfolio-call-exposure-banner"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <ShieldAlert className={`w-4 h-4 ${worst ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
        <span className="text-sm font-bold">Call exposure</span>
        <span className="text-xs text-muted-foreground">
          <strong className="text-foreground tabular-nums" data-testid="call-exposure-value">{fmtUsd(data.exposure_value)}</strong>
          {' '}({data.exposure_pct}% of portfolio) sits in {data.positions.length} above-par callable preferred{data.positions.length === 1 ? '' : 's'}.
          If every one were redeemed at par today you would lose{' '}
          <strong className="text-red-600 dark:text-red-400 tabular-nums" data-testid="call-exposure-loss">{fmtUsd(data.instant_loss)}</strong>.
        </span>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
          data-testid="call-exposure-toggle"
        >
          {open ? 'Hide' : 'Show'} positions {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>
      {open && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr className="text-left">
                <th className="py-1 pr-2">Ticker</th>
                <th className="py-1 pr-2 text-right">Shares</th>
                <th className="py-1 pr-2 text-right">Price</th>
                <th className="py-1 pr-2 text-right">Par</th>
                <th className="py-1 pr-2 text-right">Premium/sh</th>
                <th className="py-1 pr-2 text-right">Value</th>
                <th className="py-1 pr-2 text-right">Loss if called</th>
                <th className="py-1 pr-2 text-right">Annual income</th>
                <th className="py-1">Status</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {data.positions.map((p) => (
                <tr key={p.ticker} className="border-t border-border/40" data-testid={`call-exposure-row-${p.ticker}`}>
                  <td className="py-1 pr-2 font-sans">
                    <button type="button" onClick={() => onStockClick?.(p.ticker, 'insights')} className="font-bold text-primary hover:underline" title={p.name || p.ticker}>
                      {p.ticker}
                    </button>
                  </td>
                  <td className="py-1 pr-2 text-right">{Number(p.shares).toLocaleString()}</td>
                  <td className="py-1 pr-2 text-right">${Number(p.price).toFixed(2)}</td>
                  <td className="py-1 pr-2 text-right">${Number(p.par_value).toFixed(2)}</td>
                  <td className="py-1 pr-2 text-right text-red-600 dark:text-red-400">+${Number(p.premium_per_share).toFixed(2)}</td>
                  <td className="py-1 pr-2 text-right">{fmtUsd(p.market_value)}</td>
                  <td className="py-1 pr-2 text-right text-red-600 dark:text-red-400">−{fmtUsd(p.instant_loss)}</td>
                  <td className="py-1 pr-2 text-right text-emerald-700 dark:text-emerald-400">{fmtUsd(p.annual_income)}/yr</td>
                  <td className="py-1 font-sans">
                    <span className={`inline-flex px-1.5 rounded text-[9px] font-bold uppercase tracking-wide border ${p.status === 'call_soon' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-400/60' : 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border-red-400/60'}`}>
                      {statusLabel(p)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[10px] text-muted-foreground">
            Loss if called = shares × (price − par). The annual-income column shows what you give up if the issuer redeems.{' '}
            <Link to="/learn/preferred-stock-tickers#yield-to-call" className="underline decoration-dotted">How call risk and yield-to-call work →</Link>
          </p>
        </div>
      )}
    </div>
  );
}
